import type { DraggableSyntheticListeners } from '@dnd-kit/core';
import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon, GripVerticalIcon, X } from 'components/icons';
import { Button } from 'components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Tooltip } from 'components/ui/tooltip';
import { memo, useId } from 'react';
import type { HomeWidgetSize } from 'types/homeWidgets';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { SIZE_NAME, sizeLabel } from './layout';

type Props = {
    /** The widget's name, which names its group of controls. */
    name: string;
    size: HomeWidgetSize;
    /** The sizes the widget allows; with one, there is no Size menu. */
    sizes: HomeWidgetSize[];
    /** On a phone: 44px buttons and no drag handle. */
    touch: boolean;
    canMoveUp: boolean;
    canMoveDown: boolean;
    /** Whether a move one place up (`-1`) or down (`1`), or a size, would leave a gap: it is then not offered. */
    leavesGap: (change: -1 | 1 | HomeWidgetSize) => boolean;
    onMove: (by: -1 | 1) => void;
    onResize: (size: HomeWidgetSize) => void;
    onRemove: () => void;
    /** The drag handle's wiring. */
    handleRef: (element: HTMLElement | null) => void;
    handleListeners: DraggableSyntheticListeners;
    className?: string;
};

/** A size drawn on Home's three columns and four rows. */
function SizeGlyph({ size }: { size: HomeWidgetSize }) {
    const [columns, rows] = size.split('x').map(Number);
    return (
        <svg viewBox="0 0 21 24" aria-hidden className="size-6 max-sm:hidden">
            <rect x="0.5" y="0.5" width="20" height="23" rx="2" className="stroke-border-dashed fill-none" />
            <rect x="2" y="2" width={columns * 6 - 1} height={rows * 5.25 - 1} rx="1" className="fill-primary" />
        </svg>
    );
}

/**
 * One widget's controls while Home is arranged: drag, move up and down, size and remove. Drawn again only when its
 * own props change: as a drag begins, the drag's context draws every cell again several times over.
 */
export const ArrangeBar = memo(function ArrangeBar({
    name,
    size,
    sizes,
    touch,
    canMoveUp,
    canMoveDown,
    leavesGap,
    onMove,
    onResize,
    onRemove,
    handleRef,
    handleListeners,
    className,
}: Props) {
    const button = cn('text-foreground-secondary rounded-full px-0', touch ? 'h-10 w-11' : 'h-[26px] w-7');
    const rule = <span aria-hidden className="bg-border h-4 w-px" />;
    // Why a control is off: it stays in reach, to say so on hover and to a screen reader.
    const gapId = useId();
    const gapUp = leavesGap(-1);
    const gapDown = leavesGap(1);

    return (
        <div
            // A group, not a toolbar: each button is its own Tab stop, where a toolbar is one stop walked with the
            // arrow keys. `data-arrange-bar` is what the grid finds it by.
            role="group"
            aria-label={t('home.widgets.arrange', { name })}
            data-arrange-bar
            data-tour="home-widget-controls"
            className={cn(
                // `scroll-my-2`: brought into sight on its own, it keeps clear of the window's edge.
                'border-primary-wash-border bg-card text-meta text-foreground shadow-editing absolute flex scroll-my-2 items-center gap-0.5 rounded-full border px-1 whitespace-nowrap',
                touch ? 'end-2 -top-[42px] h-12' : '-top-[18px] left-1/2 h-[34px] -translate-x-1/2',
                className
            )}
        >
            {touch ? null : (
                <Tooltip content={t('home.widgets.drag')}>
                    {/* Out of the tab order: the keyboard moves a widget with the two buttons beside it. */}
                    <Button
                        ref={handleRef}
                        variant="ghost"
                        tabIndex={-1}
                        aria-label={t('home.widgets.drag')}
                        className={cn(button, 'cursor-grab touch-none')}
                        {...handleListeners}
                    >
                        <GripVerticalIcon />
                    </Button>
                </Tooltip>
            )}
            <Tooltip content={t(gapUp ? 'home.widgets.leaves-gap' : 'home.widgets.move-up')}>
                <Button
                    variant="ghost"
                    aria-label={t('home.widgets.move-up')}
                    data-move="-1"
                    disabled={!canMoveUp || gapUp}
                    focusableWhenDisabled={gapUp}
                    aria-describedby={gapUp ? gapId : undefined}
                    className={cn(button, 'aria-disabled:cursor-default aria-disabled:opacity-50')}
                    onClick={() => onMove(-1)}
                >
                    <ArrowUpIcon />
                </Button>
            </Tooltip>
            <Tooltip content={t(gapDown ? 'home.widgets.leaves-gap' : 'home.widgets.move-down')}>
                <Button
                    variant="ghost"
                    aria-label={t('home.widgets.move-down')}
                    data-move="1"
                    disabled={!canMoveDown || gapDown}
                    focusableWhenDisabled={gapDown}
                    aria-describedby={gapDown ? gapId : undefined}
                    className={cn(button, 'aria-disabled:cursor-default aria-disabled:opacity-50')}
                    onClick={() => onMove(1)}
                >
                    <ArrowDownIcon />
                </Button>
            </Tooltip>
            {sizes.length > 1 ? (
                <>
                    {rule}
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            render={
                                <Button
                                    variant="ghost"
                                    className={cn(
                                        'text-meta gap-1 rounded-full ps-2 pe-1.5',
                                        touch ? 'h-10' : 'h-[26px]'
                                    )}
                                />
                            }
                        >
                            {t('home.widgets.size', { size: t(SIZE_NAME[size]) })}
                            <ChevronDownIcon className="text-muted-foreground size-3.5" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center" className="w-55">
                            <DropdownMenuRadioGroup
                                value={size}
                                onValueChange={(value: HomeWidgetSize) => onResize(value)}
                            >
                                {sizes.map((option) => {
                                    const off = leavesGap(option);
                                    const item = (
                                        <DropdownMenuRadioItem
                                            key={option}
                                            value={option}
                                            closeOnClick
                                            disabled={off}
                                            aria-describedby={off ? gapId : undefined}
                                            className="text-meta data-checked:bg-primary-wash h-9 gap-2.5 pr-2 data-checked:font-semibold max-sm:h-11 [&>[data-slot=dropdown-menu-radio-item-indicator]]:hidden"
                                        >
                                            <SizeGlyph size={option} />
                                            <span className="flex-1">{t(SIZE_NAME[option])}</span>
                                            <span className="text-muted-foreground font-normal">
                                                {sizeLabel(option)}
                                            </span>
                                        </DropdownMenuRadioItem>
                                    );
                                    // A disabled item takes no pointer events, so the tooltip hangs off a wrapper.
                                    return off ? (
                                        <Tooltip key={option} content={t('home.widgets.leaves-gap')} side="left">
                                            <div>{item}</div>
                                        </Tooltip>
                                    ) : (
                                        item
                                    );
                                })}
                            </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </>
            ) : null}
            {rule}
            <Tooltip content={t('home.widgets.remove')}>
                <Button
                    variant="ghost"
                    aria-label={t('home.widgets.remove')}
                    className={cn(button, 'text-danger-ink hover:text-danger-ink')}
                    onClick={onRemove}
                >
                    <X />
                </Button>
            </Tooltip>
            <span id={gapId} hidden>
                {t('home.widgets.leaves-gap')}
            </span>
        </div>
    );
});
